import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { paymentAPI, assignmentAPI, projectAPI, eventAPI, crewAPI } from '../utils/api';
import { formatCurrency, formatDate } from '../utils/helpers';
import { Check, Clock, DollarSign, Users, Calendar, Briefcase, Download, ChevronRight, ChevronDown, Search } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const Payments = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [crewMembers, setCrewMembers] = useState([]);
  const [allAssignments, setAllAssignments] = useState([]);
  const [totals, setTotals] = useState({
    pendingUSD: 0, pendingINR: 0,
    paidUSD: 0, paidINR: 0
  });
  const [showExport, setShowExport] = useState(false);
  const [exportPreset, setExportPreset] = useState('this_month');
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [exportCrewId, setExportCrewId] = useState('all');

  // Search & collapsible groups
  const [search, setSearch] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  // Track whether we've initialized collapsed state for the current tab (to default groups to collapsed once loaded)
  const [initializedTabs, setInitializedTabs] = useState({});

  const toggleGroup = (key) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const isCollapsed = (key) => collapsedGroups.has(key);

  useEffect(() => {
    fetchPaymentData();
  }, []);

  // Default groups to collapsed when a tab is first viewed with data
  useEffect(() => {
    if (loading || allAssignments.length === 0) return;
    if (initializedTabs[activeTab]) return;

    // Compute keys for current tab using unfiltered assignments
    let keys = [];
    if (activeTab === 'projects') keys = Object.keys(groupByProject(allAssignments)).map(k => `projects:${k}`);
    else if (activeTab === 'months') keys = Object.keys(groupByMonth(allAssignments)).map(k => `months:${k}`);
    else if (activeTab === 'crew') keys = Object.keys(groupByCrew(allAssignments)).map(k => `crew:${k}`);
    else if (activeTab === 'yearly') keys = Object.keys(groupByYear(allAssignments)).map(k => `yearly:${k}`);

    if (keys.length > 0) {
      setCollapsedGroups(prev => {
        const next = new Set(prev);
        keys.forEach(k => next.add(k));
        return next;
      });
      setInitializedTabs(prev => ({ ...prev, [activeTab]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, allAssignments, activeTab]);

  const fetchPaymentData = async () => {
    try {
      const [projectsRes, crewRes, eventsRes] = await Promise.all([
        projectAPI.getAll(),
        crewAPI.getAll(),
        eventAPI.getAll()
      ]);
      
      setProjects(projectsRes.data);
      setCrewMembers(crewRes.data);

      // Fetch all assignments in PARALLEL (was serial before — slow with many events)
      const projectLookup = new Map(projectsRes.data.map(p => [p.project_id, p]));
      const assignmentResults = await Promise.allSettled(
        eventsRes.data.map(event => eventAPI.getAssignments(event.event_id))
      );

      let allAssignmentsData = [];
      assignmentResults.forEach((res, idx) => {
        const event = eventsRes.data[idx];
        if (res.status !== 'fulfilled') {
          console.error(`Failed to fetch assignments for event ${event.event_id}:`, res.reason);
          return;
        }
        const project = projectLookup.get(event.project_id);
        const enriched = res.value.data.map(a => ({
          ...a,
          event_name: event.event_name,
          event_type: event.event_type,
          event_date: event.event_date,
          project_id: event.project_id,
          project_name: project?.project_name || 'Unknown',
          bride_name: project?.bride_name,
          groom_name: project?.groom_name
        }));
        allAssignmentsData = allAssignmentsData.concat(enriched);
      });

      // Filter to only accepted assignments
      const acceptedAssignments = allAssignmentsData.filter(a => a.confirmation_status === 'accepted');
      setAllAssignments(acceptedAssignments);

      // Calculate totals
      let pendingUSD = 0, pendingINR = 0, paidUSD = 0, paidINR = 0;
      acceptedAssignments.forEach(a => {
        if (a.payment_status === 'paid') {
          paidUSD += a.amount_usd || 0;
          paidINR += a.amount_inr || 0;
        } else {
          pendingUSD += a.amount_usd || 0;
          pendingINR += a.amount_inr || 0;
        }
      });
      setTotals({ pendingUSD, pendingINR, paidUSD, paidINR });
    } catch (error) {
      console.error('Failed to fetch payment data:', error);
      toast.error('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async (assignmentId) => {
    try {
      await assignmentAPI.updatePayment(assignmentId, 'paid');
      toast.success('Marked as paid!');
      fetchPaymentData();
    } catch (error) {
      toast.error('Failed to update payment');
    }
  };

  const handleMarkAsPending = async (assignmentId) => {
    try {
      await assignmentAPI.updatePayment(assignmentId, 'pending');
      toast.success('Marked as pending');
      fetchPaymentData();
    } catch (error) {
      toast.error('Failed to update payment');
    }
  };

  // Group by project
  const groupByProject = (assignments) => {
    const grouped = {};
    assignments.forEach(a => {
      if (!grouped[a.project_id]) {
        grouped[a.project_id] = {
          project_name: a.project_name,
          bride_name: a.bride_name,
          groom_name: a.groom_name,
          assignments: [],
          pendingUSD: 0, paidUSD: 0, pendingINR: 0, paidINR: 0
        };
      }
      grouped[a.project_id].assignments.push(a);
      if (a.payment_status === 'paid') {
        grouped[a.project_id].paidUSD += a.amount_usd || 0;
        grouped[a.project_id].paidINR += a.amount_inr || 0;
      } else {
        grouped[a.project_id].pendingUSD += a.amount_usd || 0;
        grouped[a.project_id].pendingINR += a.amount_inr || 0;
      }
    });
    return grouped;
  };

  // Group by month
  const groupByMonth = (assignments) => {
    const grouped = {};
    assignments.forEach(a => {
      const date = new Date(a.event_date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleString('default', { month: 'long', year: 'numeric' });
      
      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          monthName,
          assignments: [],
          pendingUSD: 0, paidUSD: 0, pendingINR: 0, paidINR: 0
        };
      }
      grouped[monthKey].assignments.push(a);
      if (a.payment_status === 'paid') {
        grouped[monthKey].paidUSD += a.amount_usd || 0;
        grouped[monthKey].paidINR += a.amount_inr || 0;
      } else {
        grouped[monthKey].pendingUSD += a.amount_usd || 0;
        grouped[monthKey].pendingINR += a.amount_inr || 0;
      }
    });
    return Object.fromEntries(Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0])));
  };

  // Group by crew member
  const groupByCrew = (assignments) => {
    const grouped = {};
    assignments.forEach(a => {
      const crewId = a.crew_member_id;
      if (!grouped[crewId]) {
        grouped[crewId] = {
          crew_name: a.crew_member_name,
          assignments: [],
          pendingUSD: 0, paidUSD: 0, pendingINR: 0, paidINR: 0
        };
      }
      grouped[crewId].assignments.push(a);
      if (a.payment_status === 'paid') {
        grouped[crewId].paidUSD += a.amount_usd || 0;
        grouped[crewId].paidINR += a.amount_inr || 0;
      } else {
        grouped[crewId].pendingUSD += a.amount_usd || 0;
        grouped[crewId].pendingINR += a.amount_inr || 0;
      }
    });
    return grouped;
  };

  // Group by year with crew breakdown
  const groupByYear = (assignments) => {
    const grouped = {};
    assignments.forEach(a => {
      const date = new Date(a.event_date);
      const year = date.getFullYear().toString();
      
      if (!grouped[year]) {
        grouped[year] = {
          year,
          totalPendingUSD: 0, totalPaidUSD: 0, totalPendingINR: 0, totalPaidINR: 0,
          crewBreakdown: {}
        };
      }
      
      // Add to year totals
      if (a.payment_status === 'paid') {
        grouped[year].totalPaidUSD += a.amount_usd || 0;
        grouped[year].totalPaidINR += a.amount_inr || 0;
      } else {
        grouped[year].totalPendingUSD += a.amount_usd || 0;
        grouped[year].totalPendingINR += a.amount_inr || 0;
      }
      
      // Add to crew breakdown
      const crewId = a.crew_member_id;
      if (!grouped[year].crewBreakdown[crewId]) {
        grouped[year].crewBreakdown[crewId] = {
          crew_name: a.crew_member_name,
          pendingUSD: 0, paidUSD: 0, pendingINR: 0, paidINR: 0,
          eventCount: 0
        };
      }
      grouped[year].crewBreakdown[crewId].eventCount++;
      if (a.payment_status === 'paid') {
        grouped[year].crewBreakdown[crewId].paidUSD += a.amount_usd || 0;
        grouped[year].crewBreakdown[crewId].paidINR += a.amount_inr || 0;
      } else {
        grouped[year].crewBreakdown[crewId].pendingUSD += a.amount_usd || 0;
        grouped[year].crewBreakdown[crewId].pendingINR += a.amount_inr || 0;
      }
    });
    // Sort by year descending
    return Object.fromEntries(Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0])));
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-4 sm:p-6 lg:p-8" data-testid="payments-loading">
          <div className="mb-8">
            <div className="h-9 w-40 bg-black/5 rounded-2xl animate-pulse mb-3" />
            <div className="h-4 w-56 bg-black/5 rounded-full animate-pulse" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-2 gap-3 sm:gap-4 mb-8">
            {[0, 1].map(i => (
              <div key={i} className="card p-5 h-24 animate-pulse" />
            ))}
          </div>
          <div className="h-10 w-72 bg-black/5 rounded-full animate-pulse mb-6" />
          <div className="space-y-3">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-secondary rounded-2xl animate-pulse" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  // Filter by search term (matches crew name, event name, project name, bride/groom name)
  const searchLower = search.trim().toLowerCase();
  const filteredAssignments = searchLower
    ? allAssignments.filter(a => {
        const fields = [
          a.crew_member_name,
          a.event_name,
          a.event_type,
          a.project_name,
          a.bride_name,
          a.groom_name,
          a.event_date,
        ];
        return fields.some(f => f && String(f).toLowerCase().includes(searchLower));
      })
    : allAssignments;

  const projectData = groupByProject(filteredAssignments);
  const monthData = groupByMonth(filteredAssignments);
  const crewData = groupByCrew(filteredAssignments);
  const yearData = groupByYear(filteredAssignments);

  // Determine group keys for current tab (used by Expand/Collapse All)
  const currentGroupKeys = (() => {
    if (activeTab === 'projects') return Object.keys(projectData).map(k => `projects:${k}`);
    if (activeTab === 'months') return Object.keys(monthData).map(k => `months:${k}`);
    if (activeTab === 'crew') return Object.keys(crewData).map(k => `crew:${k}`);
    if (activeTab === 'yearly') return Object.keys(yearData).map(k => `yearly:${k}`);
    return [];
  })();

  const allCollapsed = currentGroupKeys.length > 0 && currentGroupKeys.every(k => collapsedGroups.has(k));

  const handleToggleAll = () => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (allCollapsed) {
        currentGroupKeys.forEach(k => next.delete(k));
      } else {
        currentGroupKeys.forEach(k => next.add(k));
      }
      return next;
    });
  };

  // ---- Export helpers ----
  const isoDate = (d) => {
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${yr}-${mo}-${da}`;
  };

  const computeRange = (preset) => {
    const now = new Date();
    const start = new Date(now);
    const end = new Date(now);
    if (preset === 'this_week') {
      const dow = now.getDay(); // 0 = Sun
      const diffToMon = (dow + 6) % 7; // monday-based
      start.setDate(now.getDate() - diffToMon);
      end.setDate(start.getDate() + 6);
    } else if (preset === 'last_week') {
      const dow = now.getDay();
      const diffToMon = (dow + 6) % 7;
      start.setDate(now.getDate() - diffToMon - 7);
      end.setDate(start.getDate() + 6);
    } else if (preset === 'this_month') {
      start.setDate(1);
      end.setMonth(now.getMonth() + 1, 0);
    } else if (preset === 'last_month') {
      start.setMonth(now.getMonth() - 1, 1);
      end.setMonth(now.getMonth(), 0);
    } else if (preset === 'this_year') {
      start.setMonth(0, 1);
      end.setMonth(11, 31);
    } else if (preset === 'last_year') {
      start.setFullYear(now.getFullYear() - 1, 0, 1);
      end.setFullYear(now.getFullYear() - 1, 11, 31);
    } else if (preset === 'all') {
      return { from: '', to: '' };
    } else if (preset === 'custom') {
      return { from: exportFromDate, to: exportToDate };
    }
    return { from: isoDate(start), to: isoDate(end) };
  };

  const csvEscape = (s) => {
    if (s === null || s === undefined) return '';
    const str = String(s);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const handleExport = () => {
    const { from, to } = computeRange(exportPreset);
    const rows = allAssignments.filter(a => {
      if (exportCrewId !== 'all' && a.crew_member_id !== exportCrewId) return false;
      if (from && a.event_date < from) return false;
      if (to && a.event_date > to) return false;
      return true;
    });
    rows.sort((a, b) => {
      const cmp = (a.crew_member_name || '').localeCompare(b.crew_member_name || '');
      return cmp !== 0 ? cmp : (a.event_date || '').localeCompare(b.event_date || '');
    });

    let totalPaidUSD = 0, totalPendingUSD = 0, totalPaidINR = 0, totalPendingINR = 0;
    rows.forEach(a => {
      if (a.payment_status === 'paid') {
        totalPaidUSD += a.amount_usd || 0;
        totalPaidINR += a.amount_inr || 0;
      } else {
        totalPendingUSD += a.amount_usd || 0;
        totalPendingINR += a.amount_inr || 0;
      }
    });

    const lines = [];
    lines.push(['Crew Name','Event','Project','Event Date','Day Type','Amount (USD)','Amount (INR)','Payment Status'].map(csvEscape).join(','));
    rows.forEach(a => {
      lines.push([
        a.crew_member_name || '',
        a.event_name || '',
        a.project_name || '',
        a.event_date || '',
        (a.day_type || '').replace('_',' '),
        a.amount_usd || 0,
        a.amount_inr || 0,
        a.payment_status || 'pending',
      ].map(csvEscape).join(','));
    });

    lines.push('');
    lines.push(['SUMMARY'].map(csvEscape).join(','));
    lines.push(['Date Range', from && to ? `${from} to ${to}` : (from ? `from ${from}` : (to ? `until ${to}` : 'All time'))].map(csvEscape).join(','));
    lines.push(['Crew Filter', exportCrewId === 'all' ? 'All crew' : (crewMembers.find(c => c.crew_id === exportCrewId)?.full_name || exportCrewId)].map(csvEscape).join(','));
    lines.push(['Total Events', rows.length].map(csvEscape).join(','));
    lines.push(['Total Paid (USD)', totalPaidUSD].map(csvEscape).join(','));
    lines.push(['Total Pending (USD)', totalPendingUSD].map(csvEscape).join(','));
    if (totalPaidINR > 0 || totalPendingINR > 0) {
      lines.push(['Total Paid (INR)', totalPaidINR].map(csvEscape).join(','));
      lines.push(['Total Pending (INR)', totalPendingINR].map(csvEscape).join(','));
    }

    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeCrew = exportCrewId === 'all' ? 'all-crew' : (crewMembers.find(c => c.crew_id === exportCrewId)?.full_name || 'crew').replace(/\s+/g, '-').toLowerCase();
    const safeRange = from && to ? `${from}_to_${to}` : (exportPreset || 'export');
    a.href = url;
    a.download = `payments_${safeCrew}_${safeRange}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} payment row${rows.length !== 1 ? 's' : ''}`);
    setShowExport(false);
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="payments-page">
        <div className="mb-6 sm:mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold tracking-tight mb-2">Payments</h1>
            <p className="text-base text-muted-foreground">Track and manage crew payments</p>
          </div>
          <button
            onClick={() => setShowExport(true)}
            className="flex-shrink-0 inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-full border border-border text-foreground text-xs sm:text-sm font-medium hover:bg-secondary transition-colors"
            data-testid="export-payments-btn"
          >
            <Download size={14} />
            <span className="hidden sm:inline">Export CSV</span>
            <span className="sm:hidden">Export</span>
          </button>
        </div>

        {/* Summary Cards */}
        {(() => {
          const hasINR = (totals.pendingINR > 0 || totals.paidINR > 0);
          return (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className={`grid grid-cols-2 ${hasINR ? 'lg:grid-cols-4' : 'lg:grid-cols-2'} gap-3 sm:gap-4 mb-6 sm:mb-8`}
            >
              <div className="card p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-1 sm:mb-2">
                  <Clock className="text-amber-500" size={16} />
                  <p className="text-xs font-medium text-muted-foreground">Pending</p>
                </div>
                <p className="text-xl sm:text-2xl font-mono text-amber-600 dark:text-amber-400">{formatCurrency(totals.pendingUSD, 'USD')}</p>
              </div>
              {hasINR && (
                <div className="card p-4 sm:p-5">
                  <div className="flex items-center gap-2 mb-1 sm:mb-2">
                    <Clock className="text-amber-500" size={16} />
                    <p className="text-xs font-medium text-muted-foreground">Pending INR</p>
                  </div>
                  <p className="text-xl sm:text-2xl font-mono text-amber-600 dark:text-amber-400">{formatCurrency(totals.pendingINR, 'INR')}</p>
                </div>
              )}
              <div className="card p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-1 sm:mb-2">
                  <Check className="text-green-500" size={16} />
                  <p className="text-xs font-medium text-muted-foreground">Paid</p>
                </div>
                <p className="text-xl sm:text-2xl font-mono text-green-600 dark:text-green-400">{formatCurrency(totals.paidUSD, 'USD')}</p>
              </div>
              {hasINR && (
                <div className="card p-4 sm:p-5">
                  <div className="flex items-center gap-2 mb-1 sm:mb-2">
                    <Check className="text-green-500" size={16} />
                    <p className="text-xs font-medium text-muted-foreground">Paid INR</p>
                  </div>
                  <p className="text-xl sm:text-2xl font-mono text-green-600 dark:text-green-400">{formatCurrency(totals.paidINR, 'INR')}</p>
                </div>
              )}
            </motion.div>
          );
        })()}

        {/* Tabs — iOS segmented control */}
        <div className="inline-flex gap-1 mb-4 sm:mb-6 bg-secondary rounded-full p-1 overflow-x-auto max-w-full">
          {[
            { key: 'projects', icon: Briefcase, label: 'Project' },
            { key: 'months', icon: Calendar, label: 'Month' },
            { key: 'crew', icon: Users, label: 'Crew' },
            { key: 'yearly', icon: DollarSign, label: 'Yearly' },
          ].map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-1.5 px-3 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === key
                  ? 'bg-card text-foreground shadow-[0_1px_4px_rgba(0,0,0,0.08)]'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon size={14} strokeWidth={1.75} />
              {label}
            </button>
          ))}
        </div>

        {/* Search & Expand/Collapse */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4 sm:mb-5">
          <div className="flex-1 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by crew, event, project, bride/groom…"
              className="w-full pl-9 pr-9 py-2.5 rounded-full bg-secondary border border-transparent focus:border-black/20 focus:ring-2 focus:ring-black/10 text-sm outline-none"
              data-testid="payments-search-input"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-sm px-1"
                data-testid="payments-search-clear"
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          <button
            onClick={handleToggleAll}
            disabled={currentGroupKeys.length === 0}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full border border-border text-xs font-medium hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            data-testid="payments-toggle-all-btn"
          >
            {allCollapsed ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            {allCollapsed ? 'Expand All' : 'Collapse All'}
          </button>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* BY PROJECT */}
          {activeTab === 'projects' && (
            <>
              {Object.entries(projectData).map(([projectId, data]) => {
                const key = `projects:${projectId}`;
                const collapsed = isCollapsed(key);
                return (
                  <div key={projectId} className="card p-4 sm:p-6" data-testid={`payments-group-projects-${projectId}`}>
                    <button
                      type="button"
                      onClick={() => toggleGroup(key)}
                      className="w-full mb-2 flex items-start justify-between gap-3 text-left"
                      data-testid={`payments-group-toggle-projects-${projectId}`}
                    >
                      <div className="flex-1">
                        <h3 className="text-lg sm:text-xl font-medium flex items-center gap-2">
                          {collapsed ? <ChevronRight size={18} className="text-muted-foreground" /> : <ChevronDown size={18} className="text-muted-foreground" />}
                          {data.bride_name && data.groom_name
                            ? `${data.bride_name} & ${data.groom_name}`
                            : data.project_name}
                        </h3>
                        <p className="text-sm text-muted-foreground ml-7">{data.project_name}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm ml-7">
                          <span className="text-amber-600 dark:text-amber-400 font-mono">{formatCurrency(data.pendingUSD, 'USD')} <span className="text-muted-foreground font-normal">pending</span></span>
                          <span className="text-green-600 dark:text-green-400 font-mono">{formatCurrency(data.paidUSD, 'USD')} <span className="text-muted-foreground font-normal">paid</span></span>
                          <span className="text-muted-foreground">{data.assignments.length} item{data.assignments.length !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                    </button>
                    {!collapsed && (
                      <div className="space-y-2 mt-3">
                        {data.assignments.map(a => (
                          <AssignmentRow key={a.assignment_id} assignment={a} onPaid={handleMarkAsPaid} onPending={handleMarkAsPending} isAdmin={isAdmin} showEvent />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {/* BY MONTH */}
          {activeTab === 'months' && (
            <>
              {Object.entries(monthData).map(([monthKey, data]) => {
                const key = `months:${monthKey}`;
                const collapsed = isCollapsed(key);
                return (
                  <div key={monthKey} className="card p-4 sm:p-6" data-testid={`payments-group-months-${monthKey}`}>
                    <button
                      type="button"
                      onClick={() => toggleGroup(key)}
                      className="w-full mb-2 flex items-start justify-between gap-3 text-left"
                      data-testid={`payments-group-toggle-months-${monthKey}`}
                    >
                      <div className="flex-1">
                        <h3 className="text-lg sm:text-xl font-medium flex items-center gap-2">
                          {collapsed ? <ChevronRight size={18} className="text-muted-foreground" /> : <ChevronDown size={18} className="text-muted-foreground" />}
                          {data.monthName}
                        </h3>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm ml-7">
                          <span className="text-amber-600 dark:text-amber-400 font-mono">{formatCurrency(data.pendingUSD, 'USD')} <span className="text-muted-foreground font-normal">pending</span></span>
                          <span className="text-green-600 dark:text-green-400 font-mono">{formatCurrency(data.paidUSD, 'USD')} <span className="text-muted-foreground font-normal">paid</span></span>
                          <span className="text-muted-foreground">{data.assignments.length} item{data.assignments.length !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                    </button>
                    {!collapsed && (
                      <div className="space-y-2 mt-3">
                        {data.assignments.map(a => (
                          <AssignmentRow key={a.assignment_id} assignment={a} onPaid={handleMarkAsPaid} onPending={handleMarkAsPending} isAdmin={isAdmin} showProject />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {/* BY CREW */}
          {activeTab === 'crew' && (
            <>
              {Object.entries(crewData).map(([crewId, data]) => {
                const key = `crew:${crewId}`;
                const collapsed = isCollapsed(key);
                return (
                  <div key={crewId} className="card p-4 sm:p-6" data-testid={`payments-group-crew-${crewId}`}>
                    <button
                      type="button"
                      onClick={() => toggleGroup(key)}
                      className="w-full mb-2 flex items-start justify-between gap-3 text-left"
                      data-testid={`payments-group-toggle-crew-${crewId}`}
                    >
                      <div className="flex-1">
                        <h3 className="text-lg sm:text-xl font-medium flex items-center gap-2">
                          {collapsed ? <ChevronRight size={18} className="text-muted-foreground" /> : <ChevronDown size={18} className="text-muted-foreground" />}
                          {data.crew_name}
                        </h3>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm ml-7">
                          <span className="text-amber-600 dark:text-amber-400 font-mono">{formatCurrency(data.pendingUSD, 'USD')} <span className="text-muted-foreground font-normal">pending</span></span>
                          <span className="text-green-600 dark:text-green-400 font-mono">{formatCurrency(data.paidUSD, 'USD')} <span className="text-muted-foreground font-normal">paid</span></span>
                          <span className="text-muted-foreground">{data.assignments.length} item{data.assignments.length !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                    </button>
                    {!collapsed && (
                      <div className="space-y-2 mt-3">
                        {data.assignments.map(a => (
                          <AssignmentRow key={a.assignment_id} assignment={a} onPaid={handleMarkAsPaid} onPending={handleMarkAsPending} isAdmin={isAdmin} showProject showEvent />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {/* Yearly Tab */}
          {activeTab === 'yearly' && (
            <>
              {Object.entries(yearData).map(([year, data]) => {
                const key = `yearly:${year}`;
                const collapsed = isCollapsed(key);
                return (
                  <div key={year} className="card p-4 sm:p-6" data-testid={`payments-group-yearly-${year}`}>
                    {/* Year Header with Total */}
                    <button
                      type="button"
                      onClick={() => toggleGroup(key)}
                      className="w-full mb-4 pb-4 border-b border-border text-left"
                      data-testid={`payments-group-toggle-yearly-${year}`}
                    >
                      <h3 className="text-2xl sm:text-3xl font-serif mb-3 flex items-center gap-2">
                        {collapsed ? <ChevronRight size={22} className="text-muted-foreground" /> : <ChevronDown size={22} className="text-muted-foreground" />}
                        {year}
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-amber-50 dark:bg-amber-500/10 p-3 rounded">
                          <p className="text-xs text-amber-700 dark:text-amber-400 uppercase mb-1">Total Pending</p>
                          <p className="text-lg sm:text-xl font-mono text-amber-600 dark:text-amber-400">{formatCurrency(data.totalPendingUSD, 'USD')}</p>
                          {data.totalPendingINR > 0 && (
                            <p className="text-sm font-mono text-amber-500">{formatCurrency(data.totalPendingINR, 'INR')}</p>
                          )}
                        </div>
                        <div className="bg-green-50 dark:bg-green-500/10 p-3 rounded">
                          <p className="text-xs text-green-700 dark:text-green-400 uppercase mb-1">Total Paid</p>
                          <p className="text-lg sm:text-xl font-mono text-green-600 dark:text-green-400">{formatCurrency(data.totalPaidUSD, 'USD')}</p>
                          {data.totalPaidINR > 0 && (
                            <p className="text-sm font-mono text-green-500">{formatCurrency(data.totalPaidINR, 'INR')}</p>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 text-center">
                        <p className="text-xs text-muted-foreground">YEARLY TOTAL</p>
                        <p className="text-xl sm:text-2xl font-mono font-medium">
                          {formatCurrency(data.totalPendingUSD + data.totalPaidUSD, 'USD')}
                        </p>
                      </div>
                    </button>

                    {/* Crew Breakdown */}
                    {!collapsed && (
                      <div>
                        <h4 className="text-sm uppercase tracking-wide text-muted-foreground mb-3">By Crew Member</h4>
                        <div className="space-y-3">
                          {Object.entries(data.crewBreakdown)
                            .sort((a, b) => (b[1].paidUSD + b[1].pendingUSD) - (a[1].paidUSD + a[1].pendingUSD))
                            .map(([crewId, crewData]) => (
                            <div key={crewId} className="border border-border p-3 rounded">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <div>
                                  <p className="font-medium">{crewData.crew_name}</p>
                                  <p className="text-xs text-muted-foreground">{crewData.eventCount} event{crewData.eventCount !== 1 ? 's' : ''}</p>
                                </div>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                                  <span className="text-amber-600 dark:text-amber-400 font-mono">{formatCurrency(crewData.pendingUSD, 'USD')} <span className="text-muted-foreground font-normal text-xs">pending</span></span>
                                  <span className="text-green-600 dark:text-green-400 font-mono">{formatCurrency(crewData.paidUSD, 'USD')} <span className="text-muted-foreground font-normal text-xs">paid</span></span>
                                  <span className="font-mono font-medium">{formatCurrency(crewData.pendingUSD + crewData.paidUSD, 'USD')} <span className="text-muted-foreground font-normal text-xs">total</span></span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {allAssignments.length === 0 && (
            <div className="card p-12 text-center">
              <p className="text-muted-foreground">No payments to track yet. Assign crew to events to start.</p>
            </div>
          )}

          {allAssignments.length > 0 && filteredAssignments.length === 0 && (
            <div className="card p-12 text-center" data-testid="payments-no-results">
              <p className="text-muted-foreground">No payments match "{search}".</p>
              <button onClick={() => setSearch('')} className="mt-3 text-sm text-gold hover:underline">Clear search</button>
            </div>
          )}
        </div>

        {/* Export Modal */}
        {showExport && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="export-modal">
            <div className="bg-background w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="p-5 border-b border-border flex items-center justify-between">
                <h3 className="text-lg font-medium flex items-center gap-2"><Download size={18} className="text-gold" /> Export Payments</h3>
                <button onClick={() => setShowExport(false)} className="text-muted-foreground hover:text-foreground">✕</button>
              </div>
              <div className="p-5 space-y-5">
                <div>
                  <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-2">Time Period</label>
                  <select
                    value={exportPreset}
                    onChange={(e) => setExportPreset(e.target.value)}
                    className="w-full px-3 py-2.5 border border-input bg-background text-sm"
                    data-testid="export-preset-select"
                  >
                    <option value="this_week">This Week</option>
                    <option value="last_week">Last Week</option>
                    <option value="this_month">This Month</option>
                    <option value="last_month">Last Month</option>
                    <option value="this_year">This Year</option>
                    <option value="last_year">Last Year</option>
                    <option value="custom">Custom Range</option>
                    <option value="all">All Time</option>
                  </select>
                </div>

                {exportPreset === 'custom' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-2">From</label>
                      <input
                        type="date"
                        value={exportFromDate}
                        onChange={(e) => setExportFromDate(e.target.value)}
                        className="w-full px-3 py-2.5 border border-input bg-background text-sm"
                        data-testid="export-from-date"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-2">To</label>
                      <input
                        type="date"
                        value={exportToDate}
                        onChange={(e) => setExportToDate(e.target.value)}
                        className="w-full px-3 py-2.5 border border-input bg-background text-sm"
                        data-testid="export-to-date"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-2">Crew</label>
                  <select
                    value={exportCrewId}
                    onChange={(e) => setExportCrewId(e.target.value)}
                    className="w-full px-3 py-2.5 border border-input bg-background text-sm"
                    data-testid="export-crew-select"
                  >
                    <option value="all">All Crew (overall report)</option>
                    {crewMembers.map(c => (
                      <option key={c.crew_id} value={c.crew_id}>{c.full_name} ({c.role})</option>
                    ))}
                  </select>
                </div>

                <p className="text-xs text-muted-foreground">
                  CSV includes: crew name, event, project, date, day type, amount (USD/INR), payment status. Summary totals at the bottom.
                </p>
              </div>
              <div className="p-5 border-t border-border flex gap-3">
                <button
                  onClick={() => setShowExport(false)}
                  className="flex-1 border border-border px-4 py-2.5 text-sm hover:bg-accent"
                  data-testid="export-cancel-btn"
                >
                  CANCEL
                </button>
                <button
                  onClick={handleExport}
                  className="flex-1 bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium hover:bg-black/90"
                  data-testid="export-download-btn"
                >
                  DOWNLOAD CSV
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

// Assignment Row Component
const AssignmentRow = ({ assignment, onPaid, onPending, isAdmin, showProject, showEvent }) => {
  const isPaid = assignment.payment_status === 'paid';
  
  return (
    <div className={`p-3 border ${isPaid ? 'border-green-200 dark:border-green-500/30 bg-green-50/50' : 'border-border'}`}>
      {/* Top row: name and type badge */}
      <div className="flex flex-wrap items-center gap-2 mb-1">
        {!showEvent && <span className="font-medium text-sm">{assignment.crew_member_name}</span>}
        {showEvent && <span className="font-medium text-sm">{assignment.event_name}</span>}
        <span className="text-xs px-2 py-0.5 bg-gold/10 text-gold">{assignment.day_type.replace('_', ' ')}</span>
      </div>
      
      {/* Details row */}
      <p className="text-xs text-muted-foreground mb-2">
        {showProject && <span>{assignment.bride_name} & {assignment.groom_name} • </span>}
        {showEvent && !showProject && <span>{assignment.crew_member_name} • </span>}
        {!showEvent && <span>{assignment.event_name} • </span>}
        {formatDate(assignment.event_date)}
      </p>
      
      {/* Bottom row: amount and button */}
      <div className="flex items-center justify-between">
        <div>
          <span className="font-mono text-sm">{formatCurrency(assignment.amount_usd || 0, 'USD')}</span>
          {(assignment.amount_inr || 0) > 0 && (
            <span className="font-mono text-xs text-muted-foreground ml-2">{formatCurrency(assignment.amount_inr, 'INR')}</span>
          )}
        </div>
        {isAdmin && (
          <button
            onClick={() => isPaid ? onPending(assignment.assignment_id) : onPaid(assignment.assignment_id)}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
              isPaid 
                ? 'bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400 hover:bg-green-200' 
                : 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 hover:bg-amber-200'
            }`}
          >
            {isPaid ? 'PAID' : 'MARK PAID'}
          </button>
        )}
        {!isAdmin && (
          <span className={`px-3 py-1.5 text-xs font-medium ${isPaid ? 'bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400' : 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400'}`}>
            {isPaid ? 'PAID' : 'PENDING'}
          </span>
        )}
      </div>
    </div>
  );
};

export default Payments;
